import { useMemo } from 'react';
import { TaskTableDataType } from '../components/TaskTable/types/TaskTableTypes';
import { GroupItem } from '../components/Backlog/TaskGroupSection/TaskGroupSection';
import { TaskStatus, TaskStatusGroup } from '../types/TaskStatus';

export type FlattenedRow =
  | {
      type: 'group-header';
      id: string;
      group: GroupItem;
      groupId: string;
      tasks: TaskTableDataType[];
      stats: {
        completed: number;
        total: number;
        percent: number;
      };
      isExpanded: boolean;
    }
  | {
      type: 'task';
      id: string;
      task: TaskTableDataType;
      groupId: string;
      hasSubtasks: boolean;
      isExpanded: boolean;
    }
  | {
      type: 'subtask';
      id: string;
      task: TaskTableDataType;
      parentId: number;
    }
  | {
      type: 'add-task';
      id: string;
      groupId: string;
      group: GroupItem;
    };

interface UseVirtualizedTaskDataOptions {
  tasksPerGroup: Array<{ group: GroupItem; tasks: TaskTableDataType[] }>;
  unassignedTasks: TaskTableDataType[];
  expandedGroups: Set<string>;
  expandedSubtasks: Set<number>;
  groupBy: string;
  showUnassignedGroup: boolean;
  taskStatuses: Record<number, TaskStatus>;
  hideAddTaskRow?: boolean;
}

export const useVirtualizedTaskData = ({
  tasksPerGroup,
  unassignedTasks,
  expandedGroups,
  expandedSubtasks,
  groupBy,
  showUnassignedGroup,
  taskStatuses,
  hideAddTaskRow,
}: UseVirtualizedTaskDataOptions): FlattenedRow[] => {
  return useMemo(() => {
    const rows: FlattenedRow[] = [];

    // Helper to calculate group stats. Borrowed/context parents (#2309) are shown in a
    // group they don't belong to, so they must not count toward that group's totals.
    const calculateStats = (tasks: TaskTableDataType[]) => {
      const members = tasks.filter((task) => !task.isGroupContext);
      const completed = members.filter((task) => {
        const status = taskStatuses[task.statusId];
        return status?.group === TaskStatusGroup.completed;
      }).length;
      const total = members.length;
      const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
      return { completed, total, percent };
    };

    // Process each group
    tasksPerGroup.forEach((groupData) => {
      // Hide empty groups (no rows to show) regardless of any active filter. #2501
      // Use the raw task count, not stats.total, so groups that only display a
      // borrowed context parent (#2309) are still rendered.
      if (groupData.tasks.length === 0) {
        return;
      }

      const groupId = String(groupData.group?.id ?? groupData.group ?? 'unassigned');
      const isExpanded = !expandedGroups.has(groupId);

      // Add group header
      rows.push({
        type: 'group-header',
        id: `group-${groupId}`,
        group: groupData.group,
        groupId,
        tasks: groupData.tasks,
        stats: calculateStats(groupData.tasks),
        isExpanded,
      });

      // Add tasks if group is expanded
      if (isExpanded) {
        groupData.tasks.forEach((task) => {
          const hasSubtasks = Array.isArray(task.subtasks) && task.subtasks.length > 0;
          const isTaskExpanded = expandedSubtasks.has(task.id);

          // Add parent task
          rows.push({
            type: 'task',
            id: `task-${task.id}-${groupId}`,
            task: { ...task, key: task.id },
            groupId,
            hasSubtasks,
            isExpanded: isTaskExpanded,
          });

          // Add subtasks if task is expanded
          if (hasSubtasks && isTaskExpanded) {
            task.subtasks!.forEach((subtask) => {
              rows.push({
                type: 'subtask',
                id: `subtask-${subtask.id}-${groupId}`,
                task: { ...subtask, key: subtask.id } as TaskTableDataType,
                parentId: task.id,
              });
            });
          }
        });
      }
    });

    // Add unassigned group if needed
    if (showUnassignedGroup && unassignedTasks.length > 0) {
      const noGrouping = !groupBy || groupBy === 'none';
      const groupId = 'unassigned';
      const isExpanded = noGrouping || !expandedGroups.has(groupId);

      if (!noGrouping) {
        rows.push({
          type: 'group-header',
          id: `group-${groupId}`,
          group: null,
          groupId,
          tasks: unassignedTasks,
          stats: calculateStats(unassignedTasks),
          isExpanded,
        });
      }

      if (isExpanded) {
        if (!noGrouping && unassignedTasks.length === 0 && !hideAddTaskRow) {
          rows.push({
            type: 'add-task',
            id: `add-task-${groupId}`,
            groupId,
            group: null,
          });
        }

        unassignedTasks.forEach((task) => {
          const hasSubtasks = Array.isArray(task.subtasks) && task.subtasks.length > 0;
          const isTaskExpanded = expandedSubtasks.has(task.id);

          rows.push({
            type: 'task',
            id: `task-${task.id}-${groupId}`,
            task: { ...task, key: task.id },
            groupId,
            hasSubtasks,
            isExpanded: isTaskExpanded,
          });

          if (hasSubtasks && isTaskExpanded) {
            task.subtasks!.forEach((subtask) => {
              rows.push({
                type: 'subtask',
                id: `subtask-${subtask.id}-${groupId}`,
                task: { ...subtask, key: subtask.id } as TaskTableDataType,
                parentId: task.id,
              });
            });
          }
        });
      }
    }

    return rows;
  }, [tasksPerGroup, unassignedTasks, expandedGroups, expandedSubtasks, groupBy, showUnassignedGroup, taskStatuses]);
};
